<script setup lang="ts">
defineOptions({ name: 'AuthPortal' })

import { computed } from 'vue'
import { useSso } from '../sso'

const { portalUser, links, logout } = useSso()

const systems = computed(() => [
  {
    name: 'BEST24',
    description:
      'CRM-система: клієнти, лізингові угоди, заявки та історія взаємодії.',
    icon: 'mdi-account-group-outline',
    color: '#1f4fb5',
    tint: '#eaf0fb',
    url: links.best24,
  },
  {
    name: 'Intime',
    description:
      'Облік робочого часу, відпусток, лікарняних і графіків роботи.',
    icon: 'mdi-timer-outline',
    color: '#0e7c86',
    tint: '#e3f4f5',
    url: links.intime,
  },
  {
    name: 'Doctime',
    description:
      'Електронний документообіг: погодження, підписання та архів документів.',
    icon: 'mdi-file-document-check-outline',
    color: '#5b4bc4',
    tint: '#eeecfa',
    url: links.doctime,
  },
  {
    name: 'FinReport',
    description: 'Фінансова звітність і аналітика за лізинговим портфелем.',
    icon: 'mdi-chart-box-outline',
    color: '#9aa3b2',
    tint: '#e6e9ee',
    url: links.finreport,
  },
])
</script>

<template>
  <div class="sso-portal">
    <v-sheet
      border="b"
      class="sso-portal__header"
      color="white"
      elevation="0"
      tag="header">
      <div class="sso-portal__header-content">
        <div class="sso-portal__brand">
          <span>BEST LEASING</span>
          <span class="sso-portal__brand-caption">Корпоративні системи</span>
        </div>

        <div class="sso-portal__profile">
          <div class="sso-portal__profile-text">
            <strong>{{ portalUser.name || portalUser.email }}</strong>
            <span>{{ portalUser.email }}</span>
          </div>
          <v-avatar
            :aria-label="`Профіль: ${portalUser.name || portalUser.email}`"
            class="sso-portal__avatar"
            color="blue-lighten-5"
            size="40">
            {{ portalUser.initials }}
          </v-avatar>
          <v-btn
            class="sso-portal__logout"
            prepend-icon="mdi-logout"
            variant="outlined"
            @click="logout">
            Вийти з усіх систем
          </v-btn>
        </div>
      </div>
    </v-sheet>

    <main class="sso-portal__main">
      <div class="sso-portal__intro">
        <h1>
          Добрий день<span v-if="portalUser.firstName"
            >, {{ portalUser.firstName }}</span
          >!
        </h1>
        <p>
          Оберіть систему для роботи.<span class="sso-portal__desktop-only">
            Повторно вводити пароль не потрібно.</span
          >
        </p>
      </div>

      <div class="sso-portal__systems">
        <v-card
          v-for="system in systems"
          :key="system.name"
          :aria-label="
            system.url
              ? `Відкрити ${system.name}`
              : `Немає доступу до ${system.name}`
          "
          :aria-disabled="!system.url"
          border
          :href="system.url || undefined"
          :link="!!system.url"
          :tabindex="system.url ? undefined : -1"
          class="sso-system pa-6"
          :class="{ 'sso-system--disabled': !system.url }"
          :color="system.url ? 'white' : 'blue-grey-lighten-5'"
          rounded="lg"
          variant="flat"
          @click="!system.url && $event.preventDefault()">
          <div class="sso-system__top">
            <v-avatar
              class="sso-system__icon"
              rounded="lg"
              size="52"
              :style="{ backgroundColor: system.tint, color: system.color }">
              <v-icon :icon="system.icon" size="28"></v-icon>
            </v-avatar>
            <v-chip
              class="sso-system__status"
              :prepend-icon="system.url ? undefined : 'mdi-lock-outline'"
              size="small"
              variant="flat">
              {{ system.url ? 'Доступно' : 'Немає доступу' }}
            </v-chip>
          </div>

          <div class="sso-system__content">
            <h2>{{ system.name }}</h2>
            <p>{{ system.description }}</p>
          </div>

          <div class="sso-system__action">
            <template v-if="system.url">
              Відкрити
              <v-icon icon="mdi-arrow-right" size="18"></v-icon>
            </template>
            <template v-else>Зверніться до адміністратора</template>
          </div>
        </v-card>
      </div>
    </main>

    <footer class="sso-portal__footer">
      <span class="d-flex align-center ga-2">
        <v-icon icon="mdi-lock-outline" size="16"></v-icon>
        <span class="sso-portal__desktop-only">
          Ви увійшли через єдиний вхід BEST LEASING (SSO)
        </span>
        <span class="sso-portal__mobile-only">Вхід через SSO BEST LEASING</span>
      </span>
      <span class="sso-portal__footer-legal">
        © 2026 ТОВ «БЕСТ ЛІЗИНГ»
        <template v-if="links.support">
          · <a :href="links.support">Служба підтримки</a>
        </template>
      </span>
    </footer>
  </div>
</template>
